import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import UsersApi from "../../services/api/users";
import { tokens } from "../../theme";
import { useAuth } from "../../contexts/AuthContext";

const Login = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "error",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setAlert({ show: false, message: "", severity: "error" });

    try {
      const response = await UsersApi().verifyAdmin(formData);
      if (response.success) {
        login(response.user);
        navigate("/");
      } else {
        setAlert({
          show: true,
          message: response.message || "Login failed",
          severity: "error",
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message || "An error occurred during login",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: colors.primary[100] }}
    >
      <Paper
        elevation={0}
        sx={{
          padding: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: "15px",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            height: "55px",
            width: "auto",
            paddingTop: "5px",
            paddingBottom: "5px",
          }}
          component="img"
          src="../../assets/logo.png"
          alt="Logo"
        ></Box>

        {/* <Typography
          variant="body1"
          align="center"
          mb={3}
          color={colors.secondary[300]}
        >
          Please enter your credentials to access the admin panel.
        </Typography> */}
        {alert.show && (
          <Alert severity={alert.severity} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.secondary[200],
                },
                "&:hover fieldset": {
                  borderColor: colors.primary[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },

                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
                  WebkitTextFillColor: `${colors.secondary[400]} !important`,
                  caretColor: colors.secondary[400],
                  borderRadius: "inherit",
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.secondary[400],
              },
              "& .MuiInputBase-input": {
                color: colors.secondary[400],
              },
            }}
            InputLabelProps={{
              style: { color: colors.secondary[300] },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.secondary[200],
                },
                "&:hover fieldset": {
                  borderColor: colors.primary[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },

                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
                  WebkitTextFillColor: `${colors.secondary[400]} !important`,
                  caretColor: colors.secondary[400],
                  borderRadius: "inherit",
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.secondary[400],
              },
              "& .MuiInputBase-input": {
                color: colors.secondary[400],
              },
            }}
            InputLabelProps={{
              style: { color: colors.secondary[300] },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              // mb: 2,
              padding: "12px",
              backgroundColor: colors.primary[400],
              "&:hover": {
                backgroundColor: colors.primary[300],
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
